import React from 'react';
import { FileText, CheckCircle, AlertCircle, Scale } from 'lucide-react';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumbs from '../components/seo/Breadcrumbs';

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 md:py-20">
      <SEOHead
        title="Terms & Conditions | Alden Technologies"
        description="Terms and conditions governing the use of the Alden Technologies website, quotations, orders and services."
        path="/terms-conditions"
      />
      <Breadcrumbs items={[{ label: 'Terms & Conditions' }]} />
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-12">
          <div className="text-center mb-8 md:mb-12">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Terms & Conditions</h1>
            <p className="text-gray-600">Last Updated: May 2025</p>
          </div>

          <div className="prose prose-lg max-w-none space-y-6">
            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Scale className="w-6 h-6 text-blue-600" />
                General Terms
              </h2>
              <p className="text-gray-700 mb-4">
                These Terms and Conditions govern the sale, supply, installation, and service of mineral water plants, bottling machinery, and related equipment by Alden Technologies (GST: 08BUJPR0377M1ZY) to customers in India.
              </p>
              <p className="text-gray-700">
                By placing an order, making a payment, or using our services, you agree to be bound by these terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Business Scope</h2>
              <p className="text-gray-700 mb-4">
                Alden Technologies specializes in:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Manufacturing and supply of mineral water bottling plants (24 BPM to 120 BPM)</li>
                <li>Water filling machines and packaging equipment</li>
                <li>RO water treatment systems</li>
                <li>Complete turnkey plant installation and commissioning</li>
                <li>Annual Maintenance Contracts (AMC)</li>
                <li>Spare parts supply and technical support</li>
                <li>Technical consultation and project planning</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
                Orders & Quotations
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Quotations:</strong> Valid for 30 days from the date of issue unless specified otherwise</li>
                <li><strong>Order Confirmation:</strong> Orders are confirmed only after receipt of advance payment</li>
                <li><strong>Customization:</strong> Plant specifications can be customized based on customer requirements</li>
                <li><strong>Lead Time:</strong> Manufacturing and delivery typically takes 45-90 days depending on plant capacity</li>
                <li><strong>Modifications:</strong> Changes to confirmed orders may incur additional charges and time</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Payment Terms</h2>
              <div className="bg-blue-50 p-4 md:p-6 rounded-lg mb-4">
                <h3 className="font-bold text-gray-900 mb-3">Online Payment Services Available:</h3>
                <ul className="list-disc pl-6 space-y-1 text-gray-700">
                  <li>Advance booking payments (30% of order value)</li>
                  <li>Machinery and equipment orders</li>
                  <li>Consultation charges</li>
                  <li>AMC (Annual Maintenance Contract) fees</li>
                  <li>Spare parts purchases</li>
                  <li>Project confirmation deposits</li>
                </ul>
              </div>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Advance Payment:</strong> 30-40% of total order value required for order confirmation</li>
                <li><strong>Before Dispatch:</strong> 40-50% payment before machinery dispatch</li>
                <li><strong>After Installation:</strong> Remaining 10-20% after successful installation and commissioning</li>
                <li><strong>Payment Methods:</strong> Bank transfer, online payment through PayU gateway, cheque, or demand draft</li>
                <li><strong>GST:</strong> All prices exclude GST unless mentioned. 18% GST applicable on machinery</li>
                <li><strong>Late Payment:</strong> Interest at 18% per annum may be charged on delayed payments</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Delivery & Installation</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Delivery timeline communicated at time of order confirmation</li>
                <li>Installation charges included in package unless specified otherwise</li>
                <li>Customer responsible for site preparation as per our specifications</li>
                <li>Delays due to site readiness, permits, or force majeure are not our responsibility</li>
                <li>Equipment must be inspected upon delivery; damages must be reported within 48 hours</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <AlertCircle className="w-6 h-6 text-orange-600" />
                Warranty & Support
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Standard Warranty:</strong> 1 year on manufacturing defects (electrical components: 6 months)</li>
                <li><strong>Warranty Coverage:</strong> Free repair/replacement of defective parts during warranty period</li>
                <li><strong>Exclusions:</strong> Damage due to misuse, unauthorized modifications, natural disasters, or normal wear and tear</li>
                <li><strong>AMC Available:</strong> Annual maintenance contracts available after warranty period</li>
                <li><strong>Technical Support:</strong> Remote and on-site support available</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Customer Responsibilities</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Provide accurate project requirements and site details</li>
                <li>Ensure site readiness as per our specifications before installation</li>
                <li>Obtain necessary government licenses and permits (BIS, FSSAI, etc.)</li>
                <li>Provide three-phase electricity connection and water source</li>
                <li>Arrange safe storage for delivered equipment until installation</li>
                <li>Follow operating instructions and maintenance guidelines</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Limitation of Liability</h2>
              <p className="text-gray-700">
                Alden Technologies' liability is limited to the repair or replacement of defective equipment during the warranty period. We are not liable for indirect, consequential, or incidental damages including loss of profits, business interruption, or production losses.
              </p>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Dispute Resolution</h2>
              <p className="text-gray-700 mb-4">
                Any disputes arising from these terms shall be subject to the exclusive jurisdiction of courts in Jaipur, Rajasthan. We encourage amicable resolution through negotiation before legal proceedings.
              </p>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-900">Alden Technologies</p>
                <p className="text-gray-700">KH No 588, Machara Harmada, Jaipur, Rajasthan - 302013</p>
                <p className="text-gray-700">Email: aldentechnologiesindia@gmail.com</p>
                <p className="text-gray-700">Phone: +91-9351292512</p>
                <p className="text-gray-700">GST: 08BUJPR0377M1ZY</p>
                <p className="text-gray-700">Working Hours: Monday to Saturday, 9:00 AM - 6:00 PM</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsConditions;